import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FaShoppingCart, FaArrowLeft } from "react-icons/fa";

import adminService from "../../../api/services/admin";
import { priceFormatter } from "../../../utils/utils";
import {
  SectionHeader,
  SectionTitle,
  BackButton,
  PageCard,
  PageCardBody,
  LoadingContainer,
  ErrorState,
} from "../shared/DetailPage.styles";
import {
  ModalGrid,
  OrderHeaderGrid,
  InfoLabel,
  InfoValue,
  SectionHeading,
  CustomerGrid,
  AddressSection,
  ItemsSection,
  ItemsContainer,
  OrderItem,
  ItemInfo,
  ItemTitle,
  ItemDescription,
  ItemPrice,
  OrderSummary,
  SummaryRow,
  SummaryLabel,
  SummaryValue,
  TotalRow,
} from "../../../components/OrderDetailsModal/OrderDetailsModal.styles";

const AdminOrderDetails = () => {
  const { orderId } = useParams();
  const navigate = useNavigate();

  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let mounted = true;
    setLoading(true);
    setError(false);

    adminService
      .getOrderById(orderId)
      .then((response) => {
        if (mounted) setOrder(response.order);
      })
      .catch(() => {
        if (mounted) setError(true);
      })
      .finally(() => {
        if (mounted) setLoading(false);
      });

    return () => {
      mounted = false;
    };
  }, [orderId]);

  const goBack = () => navigate("/admin-panel/porudzbine");

  if (loading) {
    return (
      <LoadingContainer>
        <l-line-spinner size="40" stroke="3" speed="1" color="var(--accent)" />
      </LoadingContainer>
    );
  }

  if (error || !order) {
    return (
      <ErrorState>
        <h3>Porudžbina nije pronađena</h3>
        <p>Porudžbina ne postoji ili je u međuvremenu obrisana.</p>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad na porudžbine
        </BackButton>
      </ErrorState>
    );
  }

  return (
    <>
      <SectionHeader>
        <BackButton onClick={goBack}>
          <FaArrowLeft />
          Nazad
        </BackButton>
        <SectionTitle>
          <FaShoppingCart />
          Detalji porudžbine
        </SectionTitle>
      </SectionHeader>

      <PageCard>
        <PageCardBody>
          <ModalGrid>
            <OrderHeaderGrid>
              <div>
                <InfoLabel>Broj porudžbine</InfoLabel>
                <InfoValue $accent>{order.orderNumber}</InfoValue>
              </div>
            </OrderHeaderGrid>

            <div>
              <SectionHeading>Informacije o kupcu</SectionHeading>
              <CustomerGrid>
                <div>
                  <InfoLabel>Ime i prezime</InfoLabel>
                  <InfoValue>
                    {order.firstName} {order.lastName}
                  </InfoValue>
                </div>
                <div>
                  <InfoLabel>Email</InfoLabel>
                  <InfoValue>{order.email}</InfoValue>
                </div>
                <div>
                  <InfoLabel>Telefon</InfoLabel>
                  <InfoValue>{order.phone}</InfoValue>
                </div>
                <div>
                  <InfoLabel>Datum narudžbe</InfoLabel>
                  <InfoValue>
                    {new Date(order.createdAt).toLocaleDateString(
                      "sr-Latn-RS",
                      {
                        year: "numeric",
                        month: "long",
                        day: "numeric",
                        hour: "2-digit",
                        minute: "2-digit",
                      },
                    )}
                  </InfoValue>
                </div>
              </CustomerGrid>
            </div>

            <div>
              <SectionHeading>Adresa dostave</SectionHeading>
              <AddressSection>
                <InfoValue>
                  {order.address}
                  <br />
                  {order.city}, {order.postalCode}
                  <br />
                  {order.country}
                </InfoValue>
              </AddressSection>
            </div>

            <ItemsSection>
              <SectionHeading>Stavke porudžbine</SectionHeading>
              <ItemsContainer>
                {order.items?.map((item, index) => (
                  <OrderItem
                    key={index}
                    $index={index}
                    $isLast={index === order.items.length - 1}
                  >
                    <ItemInfo>
                      <ItemTitle>{item.courseTitle}</ItemTitle>
                      <ItemDescription>
                        {item.courseDescription}
                      </ItemDescription>
                    </ItemInfo>
                    <ItemPrice>{priceFormatter(item.price)} RSD</ItemPrice>
                  </OrderItem>
                ))}
              </ItemsContainer>
            </ItemsSection>

            <OrderSummary>
              <SummaryRow>
                <SummaryLabel>Međuzbir:</SummaryLabel>
                <SummaryValue>
                  {priceFormatter(order.subtotal)} RSD
                </SummaryValue>
              </SummaryRow>
              {order.discount > 0 && (
                <SummaryRow>
                  <SummaryLabel>Popust:</SummaryLabel>
                  <SummaryValue $error>
                    -{priceFormatter(order.discount)} RSD
                  </SummaryValue>
                </SummaryRow>
              )}
              <TotalRow>
                <SummaryLabel>Ukupno:</SummaryLabel>
                <SummaryValue $accent>
                  {priceFormatter(order.total)} RSD
                </SummaryValue>
              </TotalRow>
            </OrderSummary>
          </ModalGrid>
        </PageCardBody>
      </PageCard>
    </>
  );
};

export default AdminOrderDetails;
